/**
 * Live transcript of an in-progress call, streamed over SSE.
 *
 * Sibling of `useInboxStream` (same fetch-based SSE mechanics — EventSource
 * can't set the Authorization header), but scoped to ONE call:
 *
 *   GET {operations}/api/voice/call-logs/:callControlId/transcript/stream
 *
 * Operations proxies the calling service, which authorizes per call and
 * replays missed segments from `Last-Event-ID` on reconnect. Every event id
 * is the segment's index in the call's transcript, monotonic per call — so
 * ordering, duplicate-drop and reconnect catch-up all ride on one number.
 *
 * The hook NEVER throws. A 403/404 (call not visible / already gone) parks it
 * as `unavailable` without retrying; transport errors back off and reconnect a
 * bounded number of times before parking with `retry()` available.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { getActiveTenantId } from '@/lib/activeTenant';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface TranscriptSegment {
  /** Monotonic per-call index — also the SSE event id. */
  seq: number;
  text: string;
  /**
   * Which side of the call spoke. Comes from Telnyx's track field; treat as
   * advisory until verified against live payloads (the CS forwards whatever
   * Telnyx sends — `transcription_track` today).
   */
  track: string | null;
  isFinal: boolean;
  /**
   * Who spoke, named by the calling service from the call's own record when
   * live call monitoring is on (customer, the agent on the call at that moment,
   * the AI receptionist, or the phone system). Absent otherwise.
   */
  speaker?: TranscriptSpeaker | null;
}

export interface TranscriptSpeaker {
  role: 'customer' | 'agent' | 'ai' | 'system' | 'unknown';
  label: string;
  userId: string | null;
}

const SPEAKER_ROLES = new Set(['customer', 'agent', 'ai', 'system', 'unknown']);

function toSpeaker(raw: unknown): TranscriptSpeaker | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.role !== 'string' || !SPEAKER_ROLES.has(value.role)) return null;
  return {
    role: value.role as TranscriptSpeaker['role'],
    label: typeof value.label === 'string' && value.label.trim() ? value.label : 'Speaker',
    userId: typeof value.userId === 'string' ? value.userId : null,
  };
}

export type TranscriptStreamStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  /** Dropped, waiting on a scheduled reconnect. */
  | 'reconnecting'
  /** Stream is over and we are done retrying — `retry()` can restart it. */
  | 'closed'
  | 'error'
  /** The server said no (403/404) — do not retry, do not show a spinner. */
  | 'unavailable';

export interface UseCallTranscriptStreamResult {
  /** Settled lines, ordered by seq, capped to the most recent MAX_SEGMENTS. */
  segments: TranscriptSegment[];
  /**
   * The in-flight partial line per speaker, newest wins. Interim results are
   * NOT settled text: each one supersedes the last for its track, so they are
   * kept apart from `segments` rather than appended — otherwise a single
   * sentence lands as "Hi", "Hi there", "Hi there how are you" forever.
   */
  interim: TranscriptSegment[];
  status: TranscriptStreamStatus;
  /** Reconnect after the stream gave up. No-op while it is still trying. */
  retry: () => void;
}

// ─── Tuning ───────────────────────────────────────────────────────────────────

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 15_000;
const JITTER_RATIO = 0.25;
/** Consecutive failures before parking. A call that ended is not coming back. */
const MAX_RECONNECT_ATTEMPTS = 6;
/**
 * Lines retained in memory/DOM. An hour-long call can produce thousands; the
 * panel only ever shows the tail, and the full record lives on the call log.
 */
const MAX_SEGMENTS = 400;

function computeBackoffMs(attempt: number): number {
  const raw = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
  const jitter = raw * JITTER_RATIO * Math.random();
  return Math.round(raw - raw * JITTER_RATIO + jitter);
}

function resolveOperationsBaseUrl(): string {
  const env = import.meta.env;
  const opsUrl = env.VITE_OPERATIONS_URL ?? '';
  return typeof opsUrl === 'string' ? opsUrl.replace(/\/+$/, '') : '';
}

function supportsStreamingFetch(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.fetch !== 'function') return false;
  if (typeof window.ReadableStream !== 'function') return false;
  if (typeof TextDecoder !== 'function') return false;
  return true;
}

// ─── SSE block parsing (id + event + data — we DO consume `id:` here) ────────

interface ParsedBlock {
  id: string | null;
  event: string | null;
  data: string;
}

function parseSseBlock(block: string): ParsedBlock {
  let id: string | null = null;
  let event: string | null = null;
  const dataLines: string[] = [];
  for (const rawLine of block.split('\n')) {
    if (rawLine.startsWith(':')) continue; // heartbeat / comment
    const colon = rawLine.indexOf(':');
    if (colon === -1) continue;
    const field = rawLine.slice(0, colon);
    const value = rawLine.slice(colon + 1).replace(/^ /, '');
    if (field === 'id') id = value;
    else if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  return { id, event, data: dataLines.join('\n') };
}

function findEventBoundary(buf: string): number {
  const lf = buf.indexOf('\n\n');
  const crlf = buf.indexOf('\r\n\r\n');
  if (lf === -1) return crlf;
  if (crlf === -1) return lf;
  return Math.min(lf, crlf);
}

/** Map a wire segment (CS forwards the Telnyx entry + seq) defensively. */
function toSegment(
  raw: Record<string, unknown>,
  fallbackSeq: number | null,
): TranscriptSegment | null {
  const seq = typeof raw.seq === 'number' && Number.isInteger(raw.seq) ? raw.seq : fallbackSeq;
  if (seq === null) return null;
  const text =
    typeof raw.text === 'string'
      ? raw.text
      : typeof raw.transcript === 'string'
        ? raw.transcript
        : '';
  if (!text.trim()) return null;
  const track =
    typeof raw.transcription_track === 'string'
      ? raw.transcription_track
      : typeof raw.track === 'string'
        ? raw.track
        : null;
  const speaker = toSpeaker(raw.speaker);
  return { seq, text, track, isFinal: raw.is_final !== false, ...(speaker ? { speaker } : {}) };
}

/** Interim lines are keyed by speaker, so a track with none drops out. */
function interimListFrom(map: Map<string, TranscriptSegment>): TranscriptSegment[] {
  return [...map.values()].sort((a, b) => a.seq - b.seq);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCallTranscriptStream(
  callControlId: string | null | undefined,
  enabled = true,
): UseCallTranscriptStreamResult {
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [interim, setInterim] = useState<TranscriptSegment[]>([]);
  const [status, setStatus] = useState<TranscriptStreamStatus>('idle');
  // Bumping this re-runs the effect, which is what `retry()` needs.
  const [retryNonce, setRetryNonce] = useState(0);
  // Reconnect cursor — survives connection churn but resets with the call id.
  const lastEventIdRef = useRef<string | null>(null);
  const statusRef = useRef<TranscriptStreamStatus>('idle');
  statusRef.current = status;

  const retry = useCallback(() => {
    // Only meaningful once we have stopped trying; otherwise a click would
    // tear down a healthy connection.
    if (statusRef.current === 'closed' || statusRef.current === 'error') {
      setRetryNonce((n) => n + 1);
    }
  }, []);

  useEffect(() => {
    // New call (or none): the previous call's text must never bleed into it.
    setSegments([]);
    setInterim([]);
    lastEventIdRef.current = null;

    if (!enabled || !callControlId) {
      setStatus('idle');
      return;
    }
    if (!supportsStreamingFetch()) {
      setStatus('error');
      return;
    }

    const abortController = new AbortController();
    let cancelled = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const seen = new Set<number>();
    const pending = new Map<string, TranscriptSegment>();

    const url = `${resolveOperationsBaseUrl()}/api/voice/call-logs/${encodeURIComponent(
      callControlId,
    )}/transcript/stream`;

    const safeSetStatus = (next: TranscriptStreamStatus) => {
      if (!cancelled) setStatus(next);
    };

    const append = (segment: TranscriptSegment) => {
      if (cancelled) return;

      // A partial line replaces the previous partial for the same speaker and
      // is never retained — the final for that track supersedes it.
      if (!segment.isFinal) {
        pending.set(segment.track ?? '_', segment);
        setInterim(interimListFrom(pending));
        return;
      }

      if (pending.delete(segment.track ?? '_')) setInterim(interimListFrom(pending));
      if (seen.has(segment.seq)) return;
      seen.add(segment.seq);

      setSegments((prev) => {
        // Replay + live can interleave at the boundary, but in-order is the
        // normal case — only pay for a sort when the stream actually jumps.
        const tail = prev[prev.length - 1];
        const next =
          tail && tail.seq > segment.seq
            ? [...prev, segment].sort((a, b) => a.seq - b.seq)
            : [...prev, segment];
        return next.length > MAX_SEGMENTS ? next.slice(next.length - MAX_SEGMENTS) : next;
      });
    };

    /** Schedule the next attempt, or park once we have burned the budget. */
    const scheduleReconnect = () => {
      if (cancelled) return;
      if (attempt >= MAX_RECONNECT_ATTEMPTS) {
        safeSetStatus('closed');
        return;
      }
      safeSetStatus('reconnecting');
      reconnectTimer = setTimeout(connect, computeBackoffMs(attempt++));
    };

    async function connect(): Promise<void> {
      if (cancelled) return;
      safeSetStatus(attempt === 0 ? 'connecting' : 'reconnecting');

      const token = useAuthStore.getState().token;
      if (!token) {
        safeSetStatus('closed');
        return;
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };
      const tenantId = getActiveTenantId();
      if (tenantId) headers['x-tenant-id'] = tenantId;
      if (lastEventIdRef.current !== null) {
        headers['Last-Event-ID'] = lastEventIdRef.current;
      }

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal: abortController.signal,
          credentials: 'omit',
          cache: 'no-store',
        });

        // The server refusing is a terminal state for this call id: the call
        // ended, isn't ours, or transcription isn't on. Retrying would only
        // hammer the proxy with requests it will keep refusing.
        if (response.status === 403 || response.status === 404) {
          safeSetStatus('unavailable');
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`transcript SSE connect failed: ${response.status}`);
        }

        attempt = 0;
        safeSetStatus('open');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        try {
          while (!abortController.signal.aborted) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let sepIdx: number;
            while ((sepIdx = findEventBoundary(buffer)) !== -1) {
              const block = buffer.slice(0, sepIdx).replace(/\r\n/g, '\n');
              const boundaryLen = buffer.startsWith('\r\n\r\n', sepIdx) ? 4 : 2;
              buffer = buffer.slice(sepIdx + boundaryLen);
              if (block.trim().length === 0) continue;
              const parsed = parseSseBlock(block);
              if (parsed.id !== null) lastEventIdRef.current = parsed.id;
              if (parsed.event !== 'transcript' || !parsed.data) continue;
              try {
                const raw = JSON.parse(parsed.data) as Record<string, unknown>;
                const fallbackSeq =
                  parsed.id !== null && Number.isInteger(parseInt(parsed.id, 10))
                    ? parseInt(parsed.id, 10)
                    : null;
                const segment = toSegment(raw, fallbackSeq);
                if (segment) append(segment);
              } catch {
                // Malformed frame — the next reconnect replay is authoritative.
              }
            }
          }
        } finally {
          try {
            reader.releaseLock();
          } catch {
            // Already released on stream error — ignore.
          }
        }

        // Clean end of stream (call over / server rotated): reconnect with the
        // cursor; if the call is gone the 404 parks us as unavailable.
        if (!cancelled && !abortController.signal.aborted) scheduleReconnect();
      } catch {
        if (cancelled || abortController.signal.aborted) return;
        scheduleReconnect();
      }
    }

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      abortController.abort();
    };
  }, [callControlId, enabled, retryNonce]);

  return { segments, interim, status, retry };
}
